import { LoginContentVariant } from '@chiroup/core/types/LoginContent.type';

type Props = {
  variant: LoginContentVariant;
};

const LoginContentPreview: React.FC<Props> = ({ variant }) => {
  const {
    layout,
    image,
    title,
    subtitle,
    content,
    buttonText,
    buttonLink,
    backgroundColor,
    accentColor,
    textCard,
  } = variant;

  const loginFormMock = (
    <div className="flex flex-col justify-center p-4">
      <div className="w-16 h-5 bg-gray-200 rounded mb-3" />
      <div className="text-xs font-bold text-gray-900 mb-0.5">Sign in</div>
      <div className="text-[8px] text-gray-500 mb-2">Or start your 14-day free trial</div>
      <div className="h-5 bg-gray-100 rounded mb-1.5" />
      <div className="h-5 bg-gray-100 rounded mb-2" />
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 border border-gray-300 rounded-sm" />
          <span className="text-[7px] text-gray-500">Remember me</span>
        </div>
        <span className="text-[7px] text-primary-500">Forgot password?</span>
      </div>
      <div className="h-6 bg-primary-500 rounded" />
    </div>
  );

  if (layout === 'none') {
    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        <div className="flex-1 bg-white dark:bg-dark-gray-900 flex items-center justify-center">
          {loginFormMock}
        </div>
      </div>
    );
  }

  if (layout === 'classic') {
    const bg = backgroundColor || '#1a2e12';
    const accent = accentColor || '#77BD43';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        {/* Marketing side */}
        <div
          className="flex-1 relative flex flex-col justify-center p-5 text-white"
          style={{
            background: image
              ? `url(${image}) center/cover`
              : `linear-gradient(135deg, ${accent} 0%, ${bg} 100%)`,
          }}
        >
          {image && (
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, ${accent}, ${bg})`, opacity: 0.8 }}
            />
          )}
          <div
            className={`relative z-10 ${textCard ? 'bg-black/30 backdrop-blur-md rounded-lg p-3' : ''}`}
          >
            <div className="text-sm font-bold mb-1">{title || 'Title'}</div>
            <div className="text-[10px] opacity-70 mb-2">{subtitle || 'Subtitle'}</div>
            <div className="text-[9px] opacity-50 leading-relaxed mb-3 line-clamp-3">
              {content || 'Content...'}
            </div>
            {buttonText && (
              <a
                href={buttonLink || '#'}
                target="_blank"
                rel="noreferrer"
                className="inline-block self-start px-3 py-1 text-[9px] border border-white/30 bg-white/15 rounded hover:bg-white/25 transition-colors"
              >
                {buttonText}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'card') {
    const bg = backgroundColor || '#f5faf0';
    const accent = accentColor || '#77BD43';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900 border-r border-gray-200 dark:border-dark-gray-600">
          {loginFormMock}
        </div>
        {/* Marketing side */}
        <div
          className="flex-1 flex items-center justify-center p-4"
          style={{ backgroundColor: bg }}
        >
          <div className="bg-white dark:bg-dark-gray-700 rounded-xl p-3 shadow-md w-full max-w-[200px]">
            {image ? (
              <img src={image} alt="" className="h-20 w-full object-cover rounded-lg mb-2" />
            ) : (
              <div
                className="h-20 rounded-lg mb-2 flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
              >
                <span className="text-[8px] text-white/60">Upload image</span>
              </div>
            )}
            <div className="text-xs font-bold text-gray-900 dark:text-dark-gray-100 mb-0.5">
              {title || 'Title'}
            </div>
            <div className="text-[9px] mb-1" style={{ color: accent }}>
              {subtitle || 'Subtitle'}
            </div>
            <div className="text-[8px] text-gray-500 leading-relaxed mb-2 line-clamp-2">
              {content || 'Content...'}
            </div>
            {buttonText && (
              <a
                href={buttonLink || '#'}
                target="_blank"
                rel="noreferrer"
                className="inline-block px-2.5 py-1 text-white text-[8px] rounded transition-colors"
                style={{ backgroundColor: accent }}
              >
                {buttonText}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'testimonial') {
    const bg = backgroundColor || '#f1f5f9';
    const accent = accentColor || '#77BD43';
    const featureList = (variant.features || '')
      .split('\n')
      .map(f => f.trim())
      .filter(Boolean)
      .slice(0, 6);
    const trustAvatars = [variant.trustAvatar1, variant.trustAvatar2, variant.trustAvatar3];
    const trustCount = variant.trustCount || '+2k';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        <div
          className="flex-1 p-3 flex flex-col justify-center overflow-hidden"
          style={{ background: `linear-gradient(180deg, ${bg} 0%, #ffffff 100%)` }}
        >
          <div className="bg-white rounded-lg shadow-md p-2.5 mb-2">
            <div className="flex gap-0.5 mb-1">
              {[0, 1, 2, 3, 4].map(i => (
                <svg
                  key={i}
                  className="w-2.5 h-2.5 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.366 2.446a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.366-2.446a1 1 0 00-1.176 0l-3.366 2.446c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                </svg>
              ))}
            </div>
            <p className="text-[9px] text-gray-800 leading-snug mb-1.5 line-clamp-2">
              &ldquo;{title || 'Add a testimonial quote.'}&rdquo;
            </p>
            <div className="flex items-center gap-1.5">
              {image ? (
                <img src={image} alt="" className="w-4 h-4 rounded-full object-cover" />
              ) : (
                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-gray-200 to-gray-400" />
              )}
              <div>
                <div className="text-[7px] font-semibold text-gray-900 leading-tight">
                  {subtitle || 'Author name'}
                </div>
                {variant.authorMeta && (
                  <div className="text-[5px] uppercase tracking-wider text-gray-500 leading-tight">
                    {variant.authorMeta}
                  </div>
                )}
              </div>
            </div>
          </div>

          {content && (
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="flex -space-x-1 shrink-0">
                {trustAvatars.map((url, i) =>
                  url ? (
                    <img
                      key={i}
                      src={url}
                      alt=""
                      className="w-3.5 h-3.5 rounded-full object-cover border border-white"
                    />
                  ) : (
                    <div
                      key={i}
                      className="w-3.5 h-3.5 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 border border-white"
                    />
                  )
                )}
                <div className="w-3.5 h-3.5 rounded-full bg-gray-100 border border-white flex items-center justify-center">
                  <span className="text-[5px] font-semibold text-gray-600">{trustCount}</span>
                </div>
              </div>
              <p className="text-[7px] text-gray-600 leading-snug line-clamp-2">{content}</p>
            </div>
          )}

          {featureList.length > 0 && (
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              {featureList.map((f, i) => (
                <div key={i} className="flex items-center gap-1">
                  <svg
                    className="w-2 h-2 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    style={{ color: accent }}
                  >
                    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.15" />
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M8 12.5l3 3 5-6"
                    />
                  </svg>
                  <span className="text-[6px] text-gray-700 truncate">{f}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (layout === 'showcase') {
    const bg = backgroundColor || '#f5faf0';
    const accent = accentColor || '#77BD43';

    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        {/* Marketing side */}
        <div className="flex-1 p-4 flex flex-col" style={{ backgroundColor: bg }}>
          <div className="text-[9px] font-medium mb-1" style={{ color: accent }}>
            {subtitle || 'Subtitle'}
          </div>
          <div className="text-sm font-extrabold text-gray-900 dark:text-dark-gray-100 leading-tight mb-1.5">
            {title || 'Title'}
          </div>
          <div className="text-[8px] text-gray-500 leading-relaxed mb-2 line-clamp-2">
            {content || 'Content...'}
          </div>
          {buttonText && (
            <a
              href={buttonLink || '#'}
              target="_blank"
              rel="noreferrer"
              className="inline-block self-start px-2.5 py-1 text-[8px] rounded transition-colors mb-2"
              style={{
                color: accent,
                borderColor: `${accent}66`,
                borderWidth: 1,
                borderStyle: 'solid',
              }}
            >
              {buttonText}
            </a>
          )}
          <div className="flex-1 min-h-0 mt-auto overflow-hidden">
            {image ? (
              <img src={image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full"
                style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'custom') {
    // Custom HTML is admin-authored content (not user-generated), rendered in an
    // iframe sandbox for isolation. The admin panel is only accessible to
    // authenticated internal staff, so XSS risk is minimal.
    return (
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
        {/* Login side */}
        <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
        {/* Custom HTML side */}
        <iframe
          className="flex-1 border-0"
          sandbox="allow-same-origin"
          title="Custom HTML preview"
          srcDoc={`<!DOCTYPE html>
<html><head>
<style>*{margin:0;padding:0;box-sizing:border-box}html,body{height:100%;overflow:hidden}body{display:flex}</style>
</head><body>${variant.customHtml || '<div style="flex:1;display:flex;align-items:center;justify-content:center;background:#f3f4f6;color:#9ca3af;font-size:12px">Custom HTML preview</div>'}</body></html>`}
        />
      </div>
    );
  }

  // full-bleed
  const bg = backgroundColor || '#77BD43';
  const accent = accentColor || '#F49F01';

  return (
    <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-dark-gray-600 h-64">
      {/* Login side */}
      <div className="flex-1 bg-white dark:bg-dark-gray-900">{loginFormMock}</div>
      {/* Marketing side */}
      <div
        className="flex-1 relative flex flex-col justify-center items-center p-4 text-white text-center"
        style={{
          background: image
            ? `url(${image}) center/cover`
            : `linear-gradient(160deg, ${bg} 0%, ${accent} 50%, ${bg}88 100%)`,
        }}
      >
        {image && <div className="absolute inset-0 bg-black/35" />}
        <div
          className={`relative z-10 ${textCard ? 'bg-black/30 backdrop-blur-md rounded-lg p-3' : ''}`}
        >
          <div className="text-xs font-bold mb-0.5">{title || 'Title'}</div>
          <div className="text-[9px] opacity-80 mb-1">{subtitle || 'Subtitle'}</div>
          <div className="text-[8px] opacity-60 leading-relaxed mb-2 line-clamp-2">
            {content || 'Content...'}
          </div>
          {buttonText && (
            <a
              href={buttonLink || '#'}
              target="_blank"
              rel="noreferrer"
              className="inline-block px-2.5 py-1 text-[8px] border border-white/30 bg-white/20 rounded hover:bg-white/30 transition-colors"
            >
              {buttonText}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginContentPreview;
